import React, { useEffect } from 'react';
import { debounce, noop, head } from 'lodash';

import { Pagination } from 'ds/components/pagination';
import { getSpacing } from 'ds/theme/spacing';
import {
  ContentHeading,
} from 'ds/components/Layout';

import { useLocale } from 'locale';
import config from 'config';
import { State as RouteState } from 'config/routes';
import { BreakpointValue, ShowAt, ShowBreakpoint, useScreenBreakpoint } from 'consts/breakpoints';

import ListingCardPlaceholder from 'components/cards/new-layout/card-placeholder';
import Footer from 'components/footer';
import { Amenity, IFiltersState, PropertyType, Seller } from 'components/filters/types';
import ProjectAdBanner from 'components/ads/ProjectAdBanner';
import ProjectAdBannerSource from 'components/ads/ProjectAdBannerSource';
import { AdGetter } from 'components/ads/types';
import { Hint } from 'components/noresults-list/styled';

import {
  CompletionType,
  IAd,
  IAutocompleteEntry,
  ListingPoiType,
  MarketplaceType,
  PoiId,
  LeadingAgentCarouselCardData,
} from 'utils/entities';

import {
  BlogWrapper,
  ListingCardsItemModeB,
} from 'screens/List/styled';
import {
  SearchContentWrapper,
  ContentControls,
  ListingCardsContainer,
  ListingsCountLabel,
  ListingSortWrapper,
  PageH1,
  ListingSeparatorH3Header,
} from 'screens/List/styledModeB';
import { ListItem } from 'screens/List/CardModeB';
import { createPageItemRenderer, createPageSwitcherRenderer } from 'ds/components/pagination/pagination-renderers';
import StickyProjectAdBanner from 'components/ads/StickyProjectAdBanner';
import AdditionalAdBannerSource from 'components/ads/AdditionalAdBannerSource';
import { CommercialBlog } from 'components/blog-carousel/search';
import { Article } from 'store/sagas/apiService/types';
import { isDefaultSearch, getLeadingAgentsPositionByScreenRange, getSingleSearchElementarySchoolDocId } from '../utils';
import SearchListingSort from 'components/search-listing-sort';
import ListViewSwitcher from 'components/list-view-switcher';
import { SearchViewMode } from 'components/list-view-switcher/constants';
import LeadingAgentsCarousel from 'components/leading-agent-carousel';
import NoListingMessage from 'components/no-listing-message/NoListingMessage';

const MOUSE_ACTIVE_DEBOUNCE_MS = 50; // 3 frames
const ADDRESS_TYPES = new Set([ CompletionType.Address ]);

export type CardPoi = Array<{ id: PoiId, type: ListingPoiType } | IAd>;

interface ListViewProps {
  page: number;
  totalPages: number;
  resolvedAddresses: IAutocompleteEntry[];
  setActive: (ids: PoiId[]) => void;
  poi: CardPoi;
  isLoading: boolean;
  total: number;
  route: RouteState;
  filtersState: IFiltersState;
  totalNearby: number;
  limit: number;
  lastInGeometryId: string;
  isSearchResultAdditionalBannerClosed: boolean;
  onAdditionalBannerClose: () => void;
  isMobileMenuHidden: boolean;
  homeTypes: PropertyType[];
  seller: Seller;
  activeFilters: Set<keyof IFiltersState>;
  articles: Article[];
  mode: SearchViewMode;
  setMode: (mode: SearchViewMode) => void;
  leadingAgents: LeadingAgentCarouselCardData[];
}

export const ListView: React.FC<ListViewProps> = (props) => {
  const {
    total,
    isLoading,
    poi,
    resolvedAddresses,
    page,
    filtersState,
    route,
    totalNearby,
    lastInGeometryId,
    totalPages,
    limit,
    isSearchResultAdditionalBannerClosed,
    onAdditionalBannerClose,
    isMobileMenuHidden,
    activeFilters,
    seller,
    homeTypes,
    articles,
    mode,
    setMode,
    leadingAgents,
  } = props;

  useEffect(() => {
    if (isLoading && !poi.length) {
      window.scrollTo(0, 0);
    }
  }, [ isLoading, poi.length ]);
  const screen = useScreenBreakpoint();
  const isTablet = screen === 3 || screen === 2;
  const { t } = useLocale();

  const setActive = debounce((poiIds: PoiId[]) => {
    props.setActive(poiIds);
  }, MOUSE_ACTIVE_DEBOUNCE_MS);


  const { dealType } = route.params;
  const { propertyTypes } = filtersState;

  const searchAreasCount = resolvedAddresses && resolvedAddresses.length;
  const isSingleAddress = searchAreasCount === 1;
  const resolvedAddress = isSingleAddress && resolvedAddresses[0];
  const isSingleAddressSearch = isSingleAddress && ADDRESS_TYPES.has(resolvedAddresses[0].type);
  const isEmptySearch = isDefaultSearch(resolvedAddresses);
  const withSafeSpace = filtersState.amenities && filtersState.amenities.length && filtersState.amenities.includes(Amenity.SecureRoom) ? ' ' : null;
  const isNoListingRedirect = route.meta && route.meta.options && route.meta.options.isNoListingRedirect;

  const pageIndex = t('searchListView.h1.currentPage', { page: page || null });
  const isSchoolSearch = isSingleAddress ? resolvedAddress.type === CompletionType.SchoolDoc : false;
  const isElementarySchoolSearchDocId = getSingleSearchElementarySchoolDocId(resolvedAddresses);
  const addressText = isSingleAddress
    ? t('searchListView.h1.addressFragment', { ...(resolvedAddress as any), cityCode: config.city })
    : resolvedAddresses && resolvedAddresses.reduce((acc, { name }, i) => `${acc}${i === 0 ? '' : t('searchListView.h1.addressFragment.multi.separator')}${name}`, '');
  const mappedDealTypeText = t('html.dealTypeFragment', { dealType });
  const singlePropertyTypeText = t('searchListView.commercial.single.propertyType', { propertyType: head(propertyTypes) });
  const mappedPropertyType = t('searchListView.commercial.multi.propertyType', { count: propertyTypes && propertyTypes.length || 0, propertyType: singlePropertyTypeText });
  const baseText = isSingleAddress
    ? t('searchListView.commercial.h1', {
      location: addressText,
      dealType: mappedDealTypeText,
      propertyType: mappedPropertyType,
      withSafeSpace,
    })
    : t('searchListView.commercial.h1.multi', {
      dealType: mappedDealTypeText,
      propertyType: mappedPropertyType,
    });

  let headingText = isSingleAddressSearch
    ? t('searchListView.h1.commercial.address', { address: resolvedAddresses[0].name, dealType, withSafeSpace })
    : baseText;

  const noFilters = activeFilters.size === 0;
  const sellerFragment = seller ? t('filters.sellerOptions', { value: seller }) : null;
  const secondSinglePropertyTypeText = homeTypes && homeTypes.length > 1 ? t('searchListView.commercial.single.propertyType', { propertyType: homeTypes[1] }) : null;
  const propertiesStr = [ singlePropertyTypeText, secondSinglePropertyTypeText ].join(', ');
  const cardView = isTablet ? SearchViewMode.Grid : mode;

  if (resolvedAddress) {
    if (noFilters) {
      if (resolvedAddress.type === CompletionType.Country) {
        headingText = t('searchListView.h1.commercial.noFilters.country', { dealType: mappedDealTypeText });
      }
      else if (resolvedAddress.type === CompletionType.City) {
        headingText = t('searchListView.h1.commercial.noFilters.city', { dealType: mappedDealTypeText, city: addressText });
      }
    }
    else if (homeTypes && homeTypes.length && seller) {
      if (homeTypes.length === 1) {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.singlePropertyType.seller.country', {
            dealType: mappedDealTypeText,
            propertyType: singlePropertyTypeText,
            sellerFragment,
          });
        }
        else if (resolvedAddress.type === CompletionType.City) {
          headingText = t('searchListView.h1.commercial.singlePropertyType.seller.city', {
            dealType: mappedDealTypeText,
            sellerFragment,
            propertyType: singlePropertyTypeText,
            city: addressText,
          });
        }
      }
      else {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.multiPropertyType.seller.country', {
            dealType: mappedDealTypeText,
            propertiesStr,
            sellerFragment,
          });
        }
        else if (resolvedAddress.type === CompletionType.City) {
          headingText = t('searchListView.h1.commercial.multiPropertyType.seller.city', {
            dealType: mappedDealTypeText,
            city: addressText,
            sellerFragment,
            propertiesStr,
          });
        }
      }
    }
    else if (homeTypes && homeTypes.length) {
      if (homeTypes.length === 1) {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.singlePropertyType.country', {
            dealType: mappedDealTypeText,
            propertyType: singlePropertyTypeText,
          });
        }
      }
      else {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.multiPropertyType.country', {
            dealType: mappedDealTypeText,
            propertiesStr,
          });
        }
      }
    }
    else if (seller) {
      if (resolvedAddress.type === CompletionType.Country) {
        headingText = t('searchListView.h1.commercial.seller.country', {
          dealType: mappedDealTypeText,
          sellerFragment,
        });
      }
      else if (resolvedAddress.type === CompletionType.City) {
        headingText = t('searchListView.h1.commercial.seller.city', {
          dealType: mappedDealTypeText,
          sellerFragment,
          city: addressText,
        });
      }
    }
  }


  const isEmptyBulletinsInGeometry = !lastInGeometryId;
  const shouldDisplayNoResults = !isLoading && !total;
  const lastPoiIndex = poi.findIndex((entry) => entry.id === lastInGeometryId);


  const renderListingsCards = (getBanner: AdGetter) => (
    <>
      <ShowAt to={2}>
        {isMobile => poi.length ? poi.map((poiItem, index) => {
          const isFirstCard = index === 0;

          const shouldShowProjectAdBanner = isMobile && index && ((index + 1) % 4 === 0);
          // this will prevent ListItem from re-rendering when new batch of listings arrive
          const { page: currentPage, id: _2, ...routeParams } = route.params;
          const totalInside = total - totalNearby;
          const prevPoi = poi[index - 1];
          const isMainBucketCard = lastPoiIndex === -1 ? (currentPage || 1) <= Math.round(totalInside / limit) : index <= lastPoiIndex;
          const showHeaderForFirstItem = currentPage === 1 && isFirstCard;
          const showInsideHeader = !isEmptyBulletinsInGeometry && showHeaderForFirstItem;
          const showNearbyHeader = (isEmptyBulletinsInGeometry && showHeaderForFirstItem) || (prevPoi && prevPoi.id === lastInGeometryId);
          const showOtherNearbyHeader = showNearbyHeader && !isSchoolSearch;
          const showSchoolInsideHeader = showInsideHeader && isSchoolSearch;
          const showSchoolNearbyHeader = showNearbyHeader && isSchoolSearch;
          const showListingSort = isFirstCard || prevPoi.id === lastInGeometryId;
          const showLeadingAgentsCarousel = leadingAgents.length && index === getLeadingAgentsPositionByScreenRange(screen, mode);

          return (
            <React.Fragment key={poiItem.id}>
              {showOtherNearbyHeader ? (
                <ListingSeparatorH3Header
                  withTopBorder={!isEmptyBulletinsInGeometry}
                >
                  {t('searchAddress.commercial.nearby.header', { dealType })}
                </ListingSeparatorH3Header>
              ) : null}
              {showSchoolNearbyHeader ? (
                <ListingSeparatorH3Header
                  data-auto="listed-school-nearby"
                  withTopBorder={!isEmptyBulletinsInGeometry}
                >
                  {t('searchAddress.school.nearby.header', { dealType })}
                </ListingSeparatorH3Header>
              ) : null}
              {showSchoolInsideHeader && isSingleAddress ? (
                <ListingSeparatorH3Header data-auto="listed-school-inside">
                  {t('searchAddress.school.inside.header', { dealType, address: resolvedAddress.name })}
                </ListingSeparatorH3Header>
              ) : null}
              {showListingSort ? (
                <>
                  <ListingSortWrapper>
                    <SearchListingSort marketplace={MarketplaceType.Residential} />
                    {isTablet || prevPoi && prevPoi.id === lastInGeometryId ? null : <ListViewSwitcher mode={mode} setMode={setMode} />}
                  </ListingSortWrapper>
                </>
              ) : null}
              <ListItem
                {...routeParams}
                allowToShowTagline
                isNearby={!isMainBucketCard}
                isSchoolSearchDocId={isElementarySchoolSearchDocId}
                id={poiItem.id}
                type={poiItem.type}
                setActive={setActive}
                listIndex={index}
                listItemPage={currentPage}
                marketplace={MarketplaceType.Commercial}
                view={cardView}
              />
              {shouldShowProjectAdBanner ? <ProjectAdBanner eventSource="list_banner_card" ad={getBanner()} mBottom={4} /> : null}
              {showLeadingAgentsCarousel ? <LeadingAgentsCarousel items={leadingAgents} source="search-page" /> : null}
              {(index === (isMobile ? 3 : 5)) && articles && articles.length ? (
                <BlogWrapper>
                  <CommercialBlog isEmptySearch={isEmptySearch} />
                </BlogWrapper>
              ) : null}
            </React.Fragment>
          );
        }) : null}
      </ShowAt>
      {isLoading ? new Array(6).fill(null).map((_, idx) => (
        <ListingCardsItemModeB key={idx} view={cardView}>
          <ListingCardPlaceholder mode={cardView} />
        </ListingCardsItemModeB>
      )) : null}
    </>
  );

  return (
    <ProjectAdBannerSource source={`${route.params.term}#${route.params.page}`}>
      {(getBanner: AdGetter) => (
        <>
          <SearchContentWrapper data-auto="listing-bulletins-block">
            <ContentControls
              data-auto="filterApplyResult"
              data-auto-bulletins-count={isSingleAddress ? total : total - totalNearby}
            >
              {isNoListingRedirect ? <NoListingMessage /> : null}
              <ContentHeading>
                <PageH1 weight="bold" data-auto="deal-type" data-auto-deal-type={dealType}>
                  {headingText}
                  {!page || page === 1 ? null : <span>{pageIndex}</span>}
                </PageH1>
              </ContentHeading>
            </ContentControls>

            {isSingleAddress ? (
              <ListingsCountLabel
                data-auto="listing-count"
                data-auto-bulletins-count={total}
              >
                {t('searchListView.h2.commercial', { dealType, total })}
              </ListingsCountLabel>
            ) : null}
            {shouldDisplayNoResults ? (
              <Hint style={{ marginTop: getSpacing(2) }}>{t('searchListView.commercial.noResults')}</Hint>
            ) : null}
            <ListingCardsContainer data-auto="listing-bulletins">
              {renderListingsCards(getBanner)}
            </ListingCardsContainer>
            {totalPages > 1 && (
              <Pagination
                totalPages={totalPages}
                onSetActivePage={noop}
                activePage={page || 1}
                paginationType={'pages'}
                renderPageItem={createPageItemRenderer(route, 'bulletins-pagination-page-')}
                renderPageSwitcher={createPageSwitcherRenderer(route, 'bulletins-pagination-')}
              />
            )}
            <ShowBreakpoint>
              {(breakpoint: BreakpointValue) => (
                <>
                  {(breakpoint >= 3 || (breakpoint <= 2 && isMobileMenuHidden)) &&
                    !isSearchResultAdditionalBannerClosed && (
                      <AdditionalAdBannerSource>
                        {(getAdditionalBanner: AdGetter) => (
                          <StickyProjectAdBanner
                            eventSource="list_sticky_banner"
                            ad={getAdditionalBanner()}
                            height="auto"
                            closable
                            onClose={onAdditionalBannerClose}
                          />
                        )}
                      </AdditionalAdBannerSource>
                    )}
                </>
              )}
            </ShowBreakpoint>
          </SearchContentWrapper>
          <Footer />
        </>
      )}
    </ProjectAdBannerSource>
  );
};
